import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";

/**
 * Script de récupération des données de société perdues
 * 
 * Ce script permet de :
 * 1. Scanner Firestore pour trouver des données orphelines
 * 2. Vérifier si des données existent encore sous d'anciennes clés
 * 3. Tenter de récupérer et restaurer les données perdues
 */

interface CompanyData {
  opportunities?: any[];
  invoices?: any[];
  devis?: any[];
  [key: string]: any;
}

interface RecoveryResult {
  found: boolean;
  oldKey?: string;
  data?: CompanyData;
  message: string;
}

/**
 * Recherche les données d'une société sous différentes variantes de clés possibles
 */
export async function searchCompanyData(
  companyName: string,
  collection: "companies" | "foodinnovCompanies" = "foodinnovCompanies"
): Promise<RecoveryResult> {
  const ref = doc(db, "prospects", collection);
  
  try {
    const docSnap = await getDoc(ref);
    if (!docSnap.exists()) {
      return {
        found: false,
        message: "Document Firestore non trouvé"
      };
    }

    const allData = docSnap.data();
    
    // Générer différentes variantes possibles de la clé
    // Note: Les clés Firestore peuvent avoir des espaces ou des underscores
    const possibleKeys = [
      companyName, // Nom exact avec espaces
      companyName.replace(/[./]/g, "_"), // Avec remplacement standard (./ -> _)
      companyName.replace(/\s+/g, "_"), // Espaces remplacés par underscores
      companyName.replace(/[./\s]/g, "_"), // Tous les caractères spéciaux remplacés
      companyName.toUpperCase(), // En majuscules avec espaces
      companyName.toLowerCase(), // En minuscules avec espaces
      companyName.toUpperCase().replace(/[./]/g, "_"), // Majuscules + remplacement ./
      companyName.toLowerCase().replace(/[./]/g, "_"), // Minuscules + remplacement ./
      companyName.toUpperCase().replace(/\s+/g, "_"), // Majuscules + espaces -> _
      companyName.toLowerCase().replace(/\s+/g, "_"), // Minuscules + espaces -> _
      companyName.toUpperCase().replace(/[./\s]/g, "_"), // Majuscules + tous remplacés
      companyName.toLowerCase().replace(/[./\s]/g, "_"), // Minuscules + tous remplacés
    ];

    // Supprimer les doublons
    const uniqueKeys = Array.from(new Set(possibleKeys));

    // Chercher dans toutes les clés possibles
    for (const key of uniqueKeys) {
      if (allData[key]) {
        const companyData = allData[key] as CompanyData;
        // Vérifier si cette clé contient des données utiles (opportunities, invoices, devis)
        const hasData = 
          (companyData.opportunities && companyData.opportunities.length > 0) ||
          (companyData.invoices && companyData.invoices.length > 0) ||
          (companyData.devis && companyData.devis.length > 0);

        if (hasData) {
          return {
            found: true,
            oldKey: key,
            data: companyData,
            message: `Données trouvées sous la clé: "${key}"`
          };
        }
      }
    }

    // Si aucune clé exacte n'est trouvée, chercher des clés similaires
    const searchName = companyName.toLowerCase().replace(/[./\s]/g, "");
    for (const [key, value] of Object.entries(allData)) {
      const keyNormalized = key.toLowerCase().replace(/[./\s]/g, "");
      if (keyNormalized.includes(searchName) || searchName.includes(keyNormalized)) {
        const companyData = value as CompanyData;
        const hasData = 
          (companyData.opportunities && companyData.opportunities.length > 0) ||
          (companyData.invoices && companyData.invoices.length > 0) ||
          (companyData.devis && companyData.devis.length > 0);

        if (hasData && companyData.name) {
          return {
            found: true,
            oldKey: key,
            data: companyData,
            message: `Données trouvées sous une clé similaire: "${key}" (nom: ${companyData.name})`
          };
        }
      }
    }

    return {
      found: false,
      message: `Aucune donnée trouvée pour "${companyName}" sous aucune variante de clé`
    };
  } catch (error: any) {
    console.error("Erreur lors de la recherche:", error);
    return {
      found: false,
      message: `Erreur: ${error.message}`
    };
  }
}

/**
 * Récupère et restaure les données d'une société vers une nouvelle clé
 */
export async function recoverCompanyData(
  oldCompanyName: string,
  newCompanyName: string,
  collection: "companies" | "foodinnovCompanies" = "foodinnovCompanies"
): Promise<{ success: boolean; message: string; recoveredData?: any }> {
  try {
    // Chercher les données
    const searchResult = await searchCompanyData(oldCompanyName, collection);
    
    if (!searchResult.found || !searchResult.data || !searchResult.oldKey) {
      return {
        success: false,
        message: searchResult.message
      };
    }

    const oldData = searchResult.data;
    // Générer la nouvelle clé en remplaçant les espaces et caractères spéciaux
    const newKey = newCompanyName.replace(/[./\s]/g, "_");
    const ref = doc(db, "prospects", collection);

    // Extraire uniquement les données à récupérer (opportunities, invoices, devis)
    const recoveredData = {
      opportunities: oldData.opportunities || [],
      invoices: oldData.invoices || [],
      devis: oldData.devis || [],
    };

    // Vérifier si la nouvelle clé existe déjà
    const docSnap = await getDoc(ref);
    if (docSnap.exists()) {
      const allData = docSnap.data();
      const existingCompany = allData[newKey];

      if (existingCompany) {
        // Fusionner les données récupérées avec les données existantes
        const mergedData = {
          ...existingCompany,
          opportunities: [
            ...(existingCompany.opportunities || []),
            ...(recoveredData.opportunities || [])
          ],
          invoices: [
            ...(existingCompany.invoices || []),
            ...(recoveredData.invoices || [])
          ],
          devis: [
            ...(existingCompany.devis || []),
            ...(recoveredData.devis || [])
          ],
        };

        await updateDoc(ref, {
          [newKey]: mergedData
        });

        return {
          success: true,
          message: `Données récupérées et fusionnées avec succès ! ${recoveredData.opportunities?.length || 0} demandes, ${recoveredData.invoices?.length || 0} factures, ${recoveredData.devis?.length || 0} devis.`,
          recoveredData: mergedData
        };
      } else {
        // La nouvelle clé n'existe pas, créer une nouvelle entrée avec les données récupérées
        await updateDoc(ref, {
          [newKey]: {
            name: newCompanyName,
            ...recoveredData
          }
        });

        return {
          success: true,
          message: `Données récupérées avec succès ! ${recoveredData.opportunities?.length || 0} demandes, ${recoveredData.invoices?.length || 0} factures, ${recoveredData.devis?.length || 0} devis.`,
          recoveredData
        };
      }
    }

    return {
      success: false,
      message: "Impossible de récupérer les données : document Firestore non trouvé"
    };
  } catch (error: any) {
    console.error("Erreur lors de la récupération:", error);
    return {
      success: false,
      message: `Erreur: ${error.message}`
    };
  }
}

/**
 * Liste toutes les sociétés avec leurs données (pour diagnostic)
 */
export async function listAllCompaniesWithData(
  collection: "companies" | "foodinnovCompanies" = "foodinnovCompanies"
): Promise<Array<{ key: string; name: string; opportunities: number; invoices: number; devis: number }>> {
  const ref = doc(db, "prospects", collection);
  
  try {
    const docSnap = await getDoc(ref);
    if (!docSnap.exists()) {
      return [];
    }

    const allData = docSnap.data();
    const results: Array<{ key: string; name: string; opportunities: number; invoices: number; devis: number }> = [];

    for (const [key, value] of Object.entries(allData)) {
      const companyData = value as CompanyData;
      if (companyData && (companyData.name || key)) {
        results.push({
          key,
          name: companyData.name || key,
          opportunities: companyData.opportunities?.length || 0,
          invoices: companyData.invoices?.length || 0,
          devis: companyData.devis?.length || 0,
        });
      }
    }

    return results.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error: any) {
    console.error("Erreur lors de la liste:", error);
    return [];
  }
}

/**
 * Fonction spécifique pour récupérer les données de "CLIENT EXPORTALIM ENR"
 */
export async function recoverExportalimData(
  collection: "companies" | "foodinnovCompanies" = "foodinnovCompanies"
): Promise<{ success: boolean; message: string; recoveredData?: any }> {
  const companyName = "CLIENT EXPORTALIM ENR";
  
  console.log(`🔍 Recherche des données pour "${companyName}"...`);
  
  // Chercher les données
  const searchResult = await searchCompanyData(companyName, collection);
  
  if (!searchResult.found) {
    return {
      success: false,
      message: `Aucune donnée trouvée pour "${companyName}". ${searchResult.message}`
    };
  }

  console.log(`✅ Données trouvées sous la clé: "${searchResult.oldKey}"`);
  console.log(`   - Demandes: ${searchResult.data?.opportunities?.length || 0}`);
  console.log(`   - Factures: ${searchResult.data?.invoices?.length || 0}`);
  console.log(`   - Devis: ${searchResult.data?.devis?.length || 0}`);

  // Récupérer vers le nouveau nom (même nom mais avec la bonne clé)
  const result = await recoverCompanyData(companyName, companyName, collection);
  
  return result;
}

